import { useState, type ReactNode } from "react";
import { Bold, Italic, Underline, List, ListOrdered, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

// Lightweight markdown-style rendering shared by the public booking form and
// the host-side editors/preview. Supported syntax:
//   **bold**       → <strong>
//   __underline__  → <u>       (checked before italic so "__" never parses as "_")
//   _italic_       → <em>
//   - item / * item → bullet list
//   1. item / 1) item → numbered list
export function renderInline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|__[^_]+__|_[^_]+_)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) parts.push(<strong key={i++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("__")) parts.push(<u key={i++}>{tok.slice(2, -2)}</u>);
    else parts.push(<em key={i++}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

const ORDERED_RE = /^\d+[.)]\s+/;

export function renderMarkdown(text: string): ReactNode {
  const lines = text.split(/\r?\n/);
  const out: ReactNode[] = [];
  let bullets: string[] = [];
  let numbered: string[] = [];
  let key = 0;
  const flush = () => {
    if (bullets.length) {
      out.push(
        <ul key={key++} className="list-disc list-inside space-y-0.5">
          {bullets.map((b, i) => <li key={i}>{renderInline(b)}</li>)}
        </ul>
      );
      bullets = [];
    }
    if (numbered.length) {
      out.push(
        <ol key={key++} className="list-decimal list-inside space-y-0.5">
          {numbered.map((b, i) => <li key={i}>{renderInline(b)}</li>)}
        </ol>
      );
      numbered = [];
    }
  };
  for (const line of lines) {
    const trimmed = line.trimStart();
    const ordered = ORDERED_RE.exec(trimmed);
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      if (numbered.length) flush();
      bullets.push(trimmed.slice(2));
    } else if (ordered) {
      if (bullets.length) flush();
      numbered.push(trimmed.slice(ordered[0].length));
    } else {
      flush();
      if (line.trim() === "") out.push(<div key={key++} className="h-1" />);
      else out.push(<p key={key++}>{renderInline(line)}</p>);
    }
  }
  flush();
  return out;
}

/** Always-visible formatting toolbar for multi-line text editors. */
export function TextToolbar({
  onBold,
  onItalic,
  onUnderline,
  onBullet,
  onNumbered,
  testId,
}: {
  onBold: () => void;
  onItalic: () => void;
  onUnderline: () => void;
  onBullet?: () => void;
  onNumbered?: () => void;
  testId: string;
}) {
  return (
    <div className="flex gap-1 mb-1" data-testid={testId}>
      <Button type="button" variant="outline" size="sm" onClick={onBold} data-testid={`${testId}-bold`} title="Bold">
        <Bold className="h-3.5 w-3.5" />
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={onItalic} data-testid={`${testId}-italic`} title="Italic">
        <Italic className="h-3.5 w-3.5" />
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={onUnderline} data-testid={`${testId}-underline`} title="Underline">
        <Underline className="h-3.5 w-3.5" />
      </Button>
      {onBullet && (
        <Button type="button" variant="outline" size="sm" onClick={onBullet} data-testid={`${testId}-bullets`} title="Bullet list">
          <List className="h-3.5 w-3.5" />
        </Button>
      )}
      {onNumbered && (
        <Button type="button" variant="outline" size="sm" onClick={onNumbered} data-testid={`${testId}-numbered`} title="Numbered list">
          <ListOrdered className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

/** Collapsible formatting style box with live rendered examples. */
export function FormattingHelp({ testId }: { testId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1" data-testid={testId}>
      <button
        type="button"
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testId}-toggle`}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Formatting help
      </button>
      {open && (
        <div className="text-xs text-muted-foreground space-y-1 bg-muted p-2 rounded mt-1" data-testid={`${testId}-body`}>
          <div><strong>bold</strong> ↔ **bold**</div>
          <div><em>italic</em> ↔ _italic_</div>
          <div><u>underline</u> ↔ __underline__</div>
          <div>bullet list ↔ - item</div>
          <div>numbered list ↔ 1. item</div>
        </div>
      )}
    </div>
  );
}
